import { homedir } from 'node:os';
import { join } from 'node:path';
import chalk from 'chalk';
import ora from 'ora';
import { api, ApiError } from '../lib/api-client.js';
import { success, error, info } from '../lib/output.js';
import { isJsonMode, jsonOutput } from '../lib/globals.js';
import {
  executeConnectivityProbe,
  type ConnectivityProbeTarget,
} from '../lib/connectivity-probe-executor.js';
import {
  buildProbeEnv,
  buildConnectivityProbeDeps,
  deriveMcpServerKey,
} from '../lib/connectivity-probe-context.js';
import type { ToolkitSourceType, ConnectivityTestOverride } from '@augmented/core/integrations';
import type { IntegrationAuthType } from '@augmented/core';

interface IntegrationSummary {
  id: string;
  name: string;
  slug: string;
  category: string;
  status: string;
  version: number;
  skills: unknown[];
  defined_scopes: unknown[];
}

interface IntegrationDetail extends IntegrationSummary {
  description: string | null;
  required_toolkits: string[];
  allowed_tools: string[];
  scripts: Record<string, string>;
  defined_scopes: Array<{
    id: string;
    name: string;
    description: string;
    default_min_role: string;
  }>;
}

interface AgentIntegrationRow {
  id: string;
  agent_id: string;
  plugin_id: string;
  plugin_version: number;
  granted_scopes: string[];
  installed_at: string;
}

interface ScopeRequest {
  id: string;
  agent_id: string;
  plugin_id: string;
  requested_scopes: string[];
  reason: string | null;
  status: string;
  requested_by: string;
  created_at: string;
}

/**
 * ENG-6472: resolve the team's agents from `GET /agents`.
 *
 * The route returns `{ agents: [...] }` (see `packages/api/src/routes/agents.ts`)
 * and the api client returns the raw body UNWRAPPED — so callers must read
 * `body.agents`, never treat the body as a bare array. Treating it as an array
 * was the `agents.find is not a function` TypeError that crashed the host-side
 * `agt integration probe` (ENG-6441) over SSM — which in turn left the xero MCP
 * presence check perpetually failing and drove mcp-presence-reaper flapping on
 * sterling. install/uninstall/enrol shared the identical latent bug. Centralised
 * here so the unwrap + non-array guard lives in exactly one place: a missing or
 * non-array `agents` degrades to `[]` (caller reports "agent not found") instead
 * of throwing.
 */
async function fetchAgents(): Promise<Array<{ agent_id: string; code_name: string }>> {
  const body = await api.get<{ agents?: Array<{ agent_id: string; code_name: string }> }>('/agents');
  return Array.isArray(body?.agents) ? body.agents : [];
}

// ── List ──────────────────────────────────────────────────────────────────

export async function integrationListCommand(): Promise<void> {
  const json = isJsonMode();
  const spinner = ora({ text: 'Fetching integrations…', isSilent: json }).start();

  try {
    const data = await api.get<IntegrationSummary[]>('/integrations/installs');
    spinner.stop();

    if (json) {
      jsonOutput({ integrations: data });
      return;
    }

    if (!data.length) {
      info('No integrations found for this team.');
      return;
    }

    console.log(chalk.bold('\nAvailable Integrations:\n'));
    for (const p of data) {
      const statusColor = p.status === 'published' ? chalk.green : p.status === 'archived' ? chalk.gray : chalk.yellow;
      console.log(
        `  ${chalk.bold(p.name)} ${chalk.dim(`(${p.slug})`)}  ${statusColor(p.status)} v${p.version}  ${chalk.dim(`${p.skills?.length ?? 0} skills, ${p.defined_scopes?.length ?? 0} scopes`)}`,
      );
    }
    console.log();
  } catch (err) {
    spinner.stop();
    handleError(err);
  }
}

// ── Show ──────────────────────────────────────────────────────────────────

export async function integrationShowCommand(slug: string): Promise<void> {
  const json = isJsonMode();
  const spinner = ora({ text: 'Fetching integration…', isSilent: json }).start();

  try {
    // List all and find by slug
    const all = await api.get<IntegrationDetail[]>('/integrations/installs');
    const integration = all.find((p) => p.slug === slug);

    if (!integration) {
      spinner.stop();
      error(`Integration "${slug}" not found.`);
      process.exitCode = 1;
      return;
    }

    // Fetch effective scopes
    const scopes = await api.get<Array<{
      id: string;
      name: string;
      effective_min_role: string;
      is_overridden: boolean;
      can_grant: boolean;
    }>>(`/integrations/installs/${integration.id}/scopes`);

    spinner.stop();

    if (json) {
      jsonOutput({ integration, scopes });
      return;
    }

    console.log(chalk.bold(`\n${integration.name}`), chalk.dim(`(${integration.slug})`));
    if (integration.description) console.log(chalk.dim(integration.description));
    console.log();
    console.log(`  Status:    ${integration.status} v${integration.version}`);
    console.log(`  Category:  ${integration.category}`);
    console.log(`  Toolkits:  ${integration.required_toolkits.join(', ') || 'none'}`);
    console.log(`  Skills:    ${integration.skills?.length ?? 0}`);

    if (scopes.length > 0) {
      console.log(chalk.bold('\n  Permission Scopes:\n'));
      for (const s of scopes) {
        const roleColor = s.can_grant ? chalk.green : chalk.red;
        const overrideTag = s.is_overridden ? chalk.cyan(' [team override]') : '';
        console.log(
          `    ${chalk.bold(s.id)}  ${s.name}  min: ${roleColor(s.effective_min_role)}${overrideTag}  ${s.can_grant ? chalk.green('✓ grantable') : chalk.red('✗ needs approval')}`,
        );
      }
    }
    console.log();
  } catch (err) {
    spinner.stop();
    handleError(err);
  }
}

// ── Probe (ENG-6441) ────────────────────────────────────────────────────────

/** The subset of `/host/agent-integrations` rows the probe needs. */
interface HostAgentIntegrationRow {
  id: string;
  definition_id: string;
  credentials: Record<string, unknown>;
  auth_type: string;
  source_type?: string | null;
  connectivity_test?: ConnectivityTestOverride | null;
}

/**
 * ENG-6441: force a FRESH host-side connectivity probe for ONE installed
 * integration and print the live verdict — NOT the cached `last_connectivity_*`
 * the central `POST /integrations/:id/test` echoes. This is the host primitive
 * the admin-debug `debug_probe_integration` tool invokes over SSM (PR B).
 *
 * Runs on the host: resolves the agent's own `.mcp.json` + `.env.integrations`
 * via the SHARED probe context (buildConnectivityProbeDeps) — the same one the
 * manager's hourly loop uses, so the on-demand verdict can't disagree with it —
 * then calls `executeConnectivityProbe` directly (bypassing the loop's due-gate,
 * which is the whole point: a fresh check on demand).
 */
export async function integrationProbeCommand(
  agentCodeName: string,
  slug: string,
): Promise<void> {
  const json = isJsonMode();
  const spinner = ora({ text: `Probing ${slug} on ${agentCodeName}…`, isSilent: json }).start();

  try {
    // 1. Resolve the agent's UUID from its code_name.
    const agents = await fetchAgents();
    const agent = agents.find((a) => a.code_name === agentCodeName);
    if (!agent) {
      spinner.stop();
      error(`Agent "${agentCodeName}" not found.`);
      process.exitCode = 1;
      return;
    }

    // 2. Fetch the agent's host integrations — decrypted credentials + the probe
    //    inputs (source_type, auth_type, connectivity_test). Host-authenticated.
    const data = await api.post<{ integrations: HostAgentIntegrationRow[] }>(
      '/host/agent-integrations',
      { agent_id: agent.agent_id },
    );
    const integrations = data.integrations ?? [];
    const integ = integrations.find(
      (i) => i.definition_id.toLowerCase() === slug.toLowerCase(),
    );
    if (!integ) {
      spinner.stop();
      const available = integrations.map((i) => i.definition_id).join(', ') || 'none';
      error(`Integration "${slug}" is not installed on ${agentCodeName}. Installed: ${available}`);
      process.exitCode = 1;
      return;
    }

    // 3. Build the shared host probe context + the single-integration target.
    const projectDir = join(homedir(), '.augmented', agentCodeName, 'project');
    const probeEnv = buildProbeEnv(projectDir);
    const probeDeps = buildConnectivityProbeDeps(projectDir, probeEnv);
    const sourceType = (integ.source_type ?? null) as ToolkitSourceType | null;
    const authType = integ.auth_type as IntegrationAuthType;
    const connectivityTest = (integ.connectivity_test ?? null) as ConnectivityTestOverride | null;
    const target: ConnectivityProbeTarget = {
      definitionId: integ.definition_id,
      sourceType,
      authType,
      credentials: integ.credentials ?? {},
      mcpServerKey: deriveMcpServerKey({
        definitionId: integ.definition_id,
        sourceType,
        authType,
        connectivityTest,
      }),
      connectivityTest,
    };

    // 4. Force a fresh probe (no due-gate, unlike the manager's rolling loop).
    const outcome = await executeConnectivityProbe(target, probeDeps);
    spinner.stop();

    // `new Date()` is fine in a one-shot CLI command (not a workflow/resume path).
    const probedAt = new Date().toISOString();

    if (outcome === null) {
      // No probe is wired for this integration kind (e.g. a built-in with no
      // remote to reach). Report it plainly rather than inventing a status.
      const result = {
        agent: agentCodeName,
        slug: integ.definition_id,
        status: 'not_probeable',
        message: 'no connectivity probe is available for this integration kind',
        probed_at: probedAt,
      };
      if (json) {
        jsonOutput(result);
        return;
      }
      info(`${integ.definition_id}: not probeable (no probe wired for this integration kind)`);
      return;
    }

    const result = {
      agent: agentCodeName,
      slug: integ.definition_id,
      status: outcome.status,
      message: outcome.message ?? null,
      probed_at: probedAt,
    };
    if (json) {
      jsonOutput(result);
      return;
    }

    const color =
      outcome.status === 'ok'
        ? chalk.green
        : outcome.status === 'down'
          ? chalk.red
          : chalk.yellow; // transient_error | degraded
    console.log(
      `${chalk.bold(integ.definition_id)} on ${chalk.bold(agentCodeName)}: ${color(outcome.status)}`,
    );
    if (outcome.message) console.log(chalk.dim(`  ${outcome.message}`));
  } catch (err) {
    spinner.stop();
    handleError(err);
  }
}

// ── Install ───────────────────────────────────────────────────────────────

export async function integrationInstallCommand(
  slug: string,
  options: { agent: string; scopes?: string },
): Promise<void> {
  const json = isJsonMode();
  const spinner = ora({ text: 'Installing integration…', isSilent: json }).start();

  try {
    // Resolve integration ID from slug
    const all = await api.get<IntegrationDetail[]>('/integrations/installs');
    const integration = all.find((p) => p.slug === slug);
    if (!integration) {
      spinner.stop();
      error(`Integration "${slug}" not found.`);
      process.exitCode = 1;
      return;
    }

    // Resolve agent ID from code name
    const agents = await fetchAgents();
    const agent = agents.find((a) => a.code_name === options.agent);
    if (!agent) {
      spinner.stop();
      error(`Agent "${options.agent}" not found.`);
      process.exitCode = 1;
      return;
    }

    const scopes = options.scopes?.split(',').map((s) => s.trim()).filter(Boolean) ?? [];

    const result = await api.post<AgentIntegrationRow>('/integrations/installs/install', {
      agent_id: agent.agent_id,
      plugin_id: integration.id,
      scopes,
    });

    spinner.stop();

    if (json) {
      jsonOutput({ agent_integration: result });
      return;
    }

    success(`Installed "${integration.name}" on agent "${options.agent}"`);
    if (scopes.length) {
      info(`Granted scopes: ${scopes.join(', ')}`);
    }
  } catch (err) {
    spinner.stop();
    if (err instanceof ApiError && err.status === 403 && err.body['needs_approval']) {
      const needsApproval = err.body['needs_approval'] as string[];
      error('Some scopes exceed your role ceiling:');
      for (const s of needsApproval) {
        console.log(chalk.yellow(`  - ${s}`));
      }
      info('To request elevated scopes, use the web dashboard or contact your team admin.');
      process.exitCode = 1;
      return;
    }
    handleError(err);
  }
}

// ── Uninstall ─────────────────────────────────────────────────────────────

export async function integrationUninstallCommand(
  slug: string,
  options: { agent: string },
): Promise<void> {
  const json = isJsonMode();
  const spinner = ora({ text: 'Uninstalling integration…', isSilent: json }).start();

  try {
    const all = await api.get<IntegrationDetail[]>('/integrations/installs');
    const integration = all.find((p) => p.slug === slug);
    if (!integration) { spinner.stop(); error(`Integration "${slug}" not found.`); process.exitCode = 1; return; }

    const agents = await fetchAgents();
    const agent = agents.find((a) => a.code_name === options.agent);
    if (!agent) { spinner.stop(); error(`Agent "${options.agent}" not found.`); process.exitCode = 1; return; }

    await api.post('/integrations/installs/uninstall', {
      agent_id: agent.agent_id,
      plugin_id: integration.id,
    });
    spinner.stop();
    success(`Uninstalled "${integration.name}" from agent "${options.agent}"`);
  } catch (err) {
    spinner.stop();
    handleError(err);
  }
}

// ── Scope Requests ────────────────────────────────────────────────────────

export async function integrationRequestsCommand(): Promise<void> {
  const json = isJsonMode();
  const spinner = ora({ text: 'Fetching scope requests…', isSilent: json }).start();

  try {
    const requests = await api.get<ScopeRequest[]>('/integrations/installs/scope-requests');
    spinner.stop();

    if (json) {
      jsonOutput({ requests });
      return;
    }

    if (!requests.length) {
      info('No pending scope requests.');
      return;
    }

    console.log(chalk.bold('\nPending Scope Requests:\n'));
    for (const r of requests) {
      console.log(`  ${chalk.bold(r.id)}  scopes: ${r.requested_scopes.join(', ')}`);
      if (r.reason) console.log(`    reason: ${chalk.dim(r.reason)}`);
      console.log(`    status: ${r.status}  requested: ${r.created_at}`);
      console.log();
    }
  } catch (err) {
    spinner.stop();
    handleError(err);
  }
}

// ── Approve ───────────────────────────────────────────────────────────────

export async function integrationApproveCommand(
  requestId: string,
  options: { notes?: string },
): Promise<void> {
  const json = isJsonMode();
  const spinner = ora({ text: 'Approving request…', isSilent: json }).start();

  try {
    const result = await api.put(`/integrations/installs/scope-requests/${requestId}`, {
      status: 'approved',
      review_notes: options.notes,
    });
    spinner.stop();

    if (json) {
      jsonOutput(result);
      return;
    }

    success(`Scope request ${requestId} approved. Scopes have been granted.`);
  } catch (err) {
    spinner.stop();
    handleError(err);
  }
}

// ── Deny ──────────────────────────────────────────────────────────────────

export async function integrationDenyCommand(
  requestId: string,
  options: { reason?: string },
): Promise<void> {
  const json = isJsonMode();
  const spinner = ora({ text: 'Denying request…', isSilent: json }).start();

  try {
    const result = await api.put(`/integrations/installs/scope-requests/${requestId}`, {
      status: 'denied',
      review_notes: options.reason,
    });
    spinner.stop();

    if (json) {
      jsonOutput(result);
      return;
    }

    success(`Scope request ${requestId} denied.`);
  } catch (err) {
    spinner.stop();
    handleError(err);
  }
}

// ── Enrol ─────────────────────────────────────────────────────────────────
//
// ENG-4962: wraps POST /integrations/ for API-key onboarding so adding a
// new integration is one command instead of raw SQL. The endpoint runs
// the ENG-4953 fail-closed encryption gate, so a credential supplied here
// is encrypted before it lands in organization_integrations /
// team_integrations / agent_integrations.
//
// OAuth integrations follow a different path (callback-driven); this
// command intentionally only covers api_key / webhook auth types where
// the secret is known at enrolment time.

interface EnrolledIntegrationRow {
  id: string;
  definition_id: string;
  status: string;
}

interface EnrolOptions {
  def: string;
  scope: 'org' | 'team' | 'agent' | 'organization';
  apiKey?: string;
  accessToken?: string;
  webhookSecret?: string;
  displayName?: string;
  agent?: string;
  authType?: 'api_key' | 'oauth2' | 'webhook';
}

export async function integrationEnrolCommand(options: EnrolOptions): Promise<void> {
  const json = isJsonMode();

  // Normalise scope: `org` is the runtime-scope vocabulary; the API
  // accepts the historical `organization` form. Both map to the same row.
  const apiScope = options.scope === 'org' ? 'organization' : options.scope;
  if (!['organization', 'team', 'agent'].includes(apiScope)) {
    error(`Invalid scope "${options.scope}". Use org, team, or agent.`);
    process.exitCode = 1;
    return;
  }

  // Build the credentials object from the secret-bearing flags. The
  // endpoint accepts {access_token} (OAuth-style) and {api_key} as
  // alternative bearer fields — exactly one secret flag should be set.
  const secrets: Array<[string, string]> = [];
  if (options.apiKey) secrets.push(['api_key', options.apiKey]);
  if (options.accessToken) secrets.push(['access_token', options.accessToken]);
  if (options.webhookSecret) secrets.push(['webhook_secret', options.webhookSecret]);
  if (secrets.length === 0) {
    error('One of --api-key, --access-token, or --webhook-secret is required.');
    process.exitCode = 1;
    return;
  }
  if (secrets.length > 1) {
    error('Only one of --api-key, --access-token, --webhook-secret may be set.');
    process.exitCode = 1;
    return;
  }
  const [secretKey, secretValue] = secrets[0]!;
  const credentials: Record<string, string> = { [secretKey]: secretValue };

  const authType = options.authType
    ?? (options.webhookSecret ? 'webhook'
      : options.apiKey ? 'api_key'
      : 'oauth2');

  const spinner = ora({ text: `Enrolling ${options.def} at ${apiScope} scope…`, isSilent: json }).start();

  try {
    // For agent scope, resolve the agent's UUID from its code_name. Team
    // scope auto-resolves from the team header; org scope derives from
    // the team's organization_id server-side (ENG-4962 endpoint tweak).
    let agentId: string | undefined;
    if (apiScope === 'agent') {
      if (!options.agent) {
        spinner.stop();
        error('--agent <code-name> is required for agent scope.');
        process.exitCode = 1;
        return;
      }
      const agents = await fetchAgents();
      const found = agents.find((a) => a.code_name === options.agent);
      if (!found) {
        spinner.stop();
        error(`Agent "${options.agent}" not found.`);
        process.exitCode = 1;
        return;
      }
      agentId = found.agent_id;
    }

    // Resolve a display_name from the definition if the caller didn't
    // supply one. Saves the user from having to type "Resend" twice.
    let displayName = options.displayName;
    if (!displayName) {
      const defs = await api.get<IntegrationDetail[]>('/integrations/installs');
      const def = defs.find((d) => d.slug === options.def);
      displayName = def?.name ?? options.def;
    }

    const result = await api.post<{ integration: EnrolledIntegrationRow }>('/integrations', {
      scope: apiScope,
      definition_id: options.def,
      display_name: displayName,
      auth_type: authType,
      credentials,
      config: {},
      ...(agentId ? { agent_id: agentId } : {}),
    });

    spinner.stop();

    const row = result.integration;
    if (json) {
      jsonOutput({ integration: row });
      return;
    }

    success(`Enrolled "${displayName}" (${options.def}) at ${apiScope} scope`);
    info(`Integration ID: ${row.id}`);
    info(`Status:         ${row.status}`);
  } catch (err) {
    spinner.stop();
    handleError(err);
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────

function handleError(err: unknown): void {
  if (err instanceof ApiError) {
    error(`API error (${err.status}): ${err.message}`);
  } else {
    error((err as Error).message);
  }
  process.exitCode = 1;
}
